import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Eye, CursorClick, ArrowBendUpRight, Fire } from "@phosphor-icons/react";
import { OUTCOMES } from "./CampaignParts";
import { fmtDateTime } from "./render";

const recState = (r) => (r.status === "sent" ? "envoyé" : r.status === "failed" ? "échec" : r.status === "pending" ? "non envoyé" : r.status);
const fuState = (s) => (s === "sent" ? "envoyée" : s === "skipped" ? "écartée" : s === "sending" ? "en cours" : "échec");
const heat = (r) => (r.outcome === "replied_interested" ? 4 : r.clicks ? 3 : r.opens ? 2 : r.status === "sent" ? 1 : 0);
const pct = (n, d) => (d ? `${Math.round((100 * n) / d)} %` : "—");
const FILTERS = { all: "Tous", clicked: "Ont cliqué", opened: "Ont ouvert, sans clic", none: "Sans réaction" };

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-border rounded-md px-3 py-2 min-w-[120px]" data-testid={testid}>
      <div className="text-[11px] text-muted flex items-center gap-1">{Icon && <Icon size={12} />} {label}</div>
      <div className="text-lg font-semibold text-fg">{value} <span className="text-xs font-normal text-muted">{sub}</span></div>
    </div>
  );
}

function Heat({ r }) {
  if (r.clicks) return <span className="text-[10px] rounded-full px-2 py-0.5 bg-accent/15 text-accent flex items-center gap-1 w-fit"><Fire size={10} weight="fill" /> a cliqué</span>;
  if (r.opens) return <span className="text-[10px] rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-500 w-fit">a ouvert</span>;
  return null;
}

// Campagne envoyée : ouvertures, clics et réponses par destinataire, triés par intérêt ; relance ciblée des plus intéressés.
export function SentRecipients({ c, reload, onOpen }) {
  const [order, setOrder] = useState("heat");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const e = c.engagement || { sent: 0, opened: 0, clicked: 0, tracked: 0 };
  const rows = useMemo(() => {
    const list = c.recipients.filter((r) => filter === "all" || (filter === "clicked" ? r.clicks : filter === "opened" ? r.opens && !r.clicks : r.status === "sent" && !r.opens));
    return order === "heat" ? [...list].sort((a, b) => heat(b) - heat(a) || (b.clicks || 0) - (a.clicks || 0) || (b.opens || 0) - (a.opens || 0)) : list;
  }, [c.recipients, order, filter]);
  const eligible = (seg) => c.recipients.filter((r) => r.status === "sent" && !r.outcome && (seg === "clicked" ? r.clicks : r.opens || r.clicks));
  const mark = async (r, outcome) => {
    try { await api.post(`/admin/leads/campaigns/${c.id}/recipients/${r.siren}/mark`, { outcome, key: r.key }); toast.success(OUTCOMES[outcome]); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const relaunch = async (seg) => {
    const list = eligible(seg);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/campaigns", { name: `Relance — ${c.name}`.slice(0, 120), sirens: [...new Set(list.map((r) => r.siren))],
        selection: list.map((r) => r.key), subject: `Re : ${c.subject}`.slice(0, 200), body_html: c.body_html, source_campaign_id: c.id, segment: seg });
      toast.success(`Brouillon de relance créé : ${list.length} contact(s) déjà cochés`); onOpen?.(data.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const nClicked = eligible("clicked").length, nEngaged = eligible("engaged").length;
  return (
    <div className="surface rounded-lg p-4 md:p-5 space-y-3" data-testid="lead-campaign-recipients">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="overline">Destinataires ({c.recipients.length})</div>
        {c.sender && <span className="text-[11px] text-muted">Expéditeur : {c.sender.email}</span>}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="lead-engagement-stats">
        <Stat label="Envoyés" value={e.sent} testid="lead-stat-sent" />
        <Stat icon={Eye} label="Ouverts" value={e.opened} sub={pct(e.opened, e.sent)} testid="lead-stat-opened" />
        <Stat icon={CursorClick} label="Ont cliqué" value={e.clicked} sub={pct(e.clicked, e.sent)} testid="lead-stat-clicked" />
        <Stat label="Réponses" value={c.recipients.filter((r) => (r.outcome || "").startsWith("replied")).length} testid="lead-stat-replied" />
      </div>
      <p className="text-[11px] text-muted" data-testid="lead-engagement-note">
        {e.tracked ? "Ouvertures indicatives : certaines messageries bloquent les images, d'autres les chargent automatiquement. Un clic compte aussi comme une ouverture."
          : "Suivi des ouvertures et des clics non disponible : campagne envoyée avant l'activation du suivi."}
      </p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select value={order} onChange={(ev) => setOrder(ev.target.value)} className="bg-surface-2 border border-border rounded-md px-2 py-1.5" data-testid="lead-sent-order">
          <option value="heat">Les plus intéressés d'abord</option><option value="sent">Ordre d'envoi</option>
        </select>
        <select value={filter} onChange={(ev) => setFilter(ev.target.value)} className="bg-surface-2 border border-border rounded-md px-2 py-1.5" data-testid="lead-sent-filter">
          {Object.entries(FILTERS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="flex-1" />
        <button disabled={busy || !nClicked} onClick={() => relaunch("clicked")} className="border border-accent/40 text-accent rounded-md px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-40" data-testid="lead-relaunch-clicked">
          <ArrowBendUpRight size={13} /> Relancer ceux qui ont cliqué ({nClicked})
        </button>
        <button disabled={busy || !nEngaged} onClick={() => relaunch("engaged")} className="border border-border rounded-md px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-40" data-testid="lead-relaunch-engaged">
          <ArrowBendUpRight size={13} /> Relancer ceux qui ont ouvert ou cliqué ({nEngaged})
        </button>
      </div>
      <div className="overflow-x-auto border border-border rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-surface-2 text-muted"><tr>{["Contact", "Adresse", "Envoi", "Ouvertures", "Clics", "Réponse"].map((h) => <th key={h} className="text-left p-2 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key || r.siren} className="border-t border-border/60" data-testid={`lead-recipient-${r.key || r.siren}`}>
                <td className="p-2 min-w-[150px]"><div className="text-fg">{r.contact_name || "—"}</div><div className="text-[10px] text-muted">{r.company}</div><Heat r={r} /></td>
                <td className="p-2 font-mono text-[11px] whitespace-nowrap">{r.email}</td>
                <td className={`p-2 whitespace-nowrap ${r.status === "sent" ? "text-accent" : r.status === "failed" ? "text-destructive" : "text-muted"}`}>{recState(r)}{r.followup_status ? ` · relance ${fuState(r.followup_status)}` : ""}</td>
                <td className="p-2 whitespace-nowrap" data-testid={`lead-recipient-opens-${r.key}`}>{r.opens ? <><b className="text-fg">{r.opens}</b> <span className="text-[10px] text-muted">· {fmtDateTime(r.last_open_at)}</span></> : <span className="text-muted">—</span>}</td>
                <td className="p-2 whitespace-nowrap" data-testid={`lead-recipient-clicks-${r.key}`} title={(r.clicked_urls || []).join("\n")}>{r.clicks ? <><b className="text-accent">{r.clicks}</b> <span className="text-[10px] text-muted">· {fmtDateTime(r.last_click_at)}</span></> : <span className="text-muted">—</span>}</td>
                <td className="p-2">{r.status === "sent" && (
                  <select value={r.outcome || ""} onChange={(ev) => ev.target.value && mark(r, ev.target.value)} data-testid={`lead-recipient-outcome-${r.key || r.siren}`}
                    className="bg-surface-2 border border-border rounded text-[11px] px-1.5 py-0.5 text-muted">
                    <option value="">Aucune</option>{Object.entries(OUTCOMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-4 text-center text-muted">Aucun destinataire pour ce filtre.</td></tr>}
          </tbody>
        </table>
      </div>
      {c.skipped?.length > 0 && <div className="pt-1"><div className="text-[11px] text-muted mb-1">Écartés ({c.skipped.length})</div>
        {c.skipped.map((s) => <div key={s.key || s.siren} className="text-[11px] text-muted">{s.contact_name ? `${s.contact_name} · ` : ""}{s.name} — {s.reason}</div>)}</div>}
    </div>
  );
}
